import React, { Dispatch, FunctionComponent, SetStateAction, useEffect } from 'react';
import { Field, Form, useFormikContext } from 'formik';

import { Button, CurrencyField, FormGroup, SelectDropdownField, StepperField, Text } from '@tradingblock/components';
import { FormValues } from '../views/FundingRequestStep';
import { Constraints } from '@tradingblock/types';

interface FundingRequestStepFormProps {
  iraConstraints: Constraints | undefined;
  showErrorSubmittingFundingRequest: boolean;
  setShowApplyToPriorYearField: Dispatch<SetStateAction<boolean>>;
  showApplyToPriorYearField: boolean;
};

const FundingRequestStepForm: FunctionComponent<FundingRequestStepFormProps> = ({
  iraConstraints,
  setShowApplyToPriorYearField,
  showErrorSubmittingFundingRequest,
  showApplyToPriorYearField,
}) => {
  const { values } = useFormikContext<FormValues>();

  useEffect(() => {
    if (iraConstraints) {
      if (
        iraConstraints.contribution.canMakeContributionsForPriorYear &&
        iraConstraints
        .contribution
        .ach
        .allowedTypes
        .map(
          (allowedType) =>
            allowedType.type === values.transferIraContributionType ? allowedType : { type: null, previousYearContributionAllowed: null })[0].previousYearContributionAllowed
      ) {
        setShowApplyToPriorYearField(true);
      } else {
        setShowApplyToPriorYearField(false);
      }
    }
  }, [values.transferIraContributionType, iraConstraints]);

  return (
    <Form translate={undefined}>
      <FormGroup>
        <Field
          component={CurrencyField}
          id="amount"
          placeholder={<Text id="fundYourAccountComponent.depositAmount" />}
        />
      </FormGroup>

      {(iraConstraints && (
        <>
          <FormGroup help={<Text id={`transferIraContributionTypeDescriptions.${values.transferIraContributionType}`} />}>
            <Field
              component={SelectDropdownField}
              placeholder={<Text id="fundYourAccountComponent.iraContributionType" />}
              options={iraConstraints.contribution.ach.allowedTypes.map((allowedType) => allowedType.type)}
              useRawOptions
              rawOptionLabels={iraConstraints.contribution.ach.allowedTypes.reduce((acc, curr) => ({ ...acc, [curr.type]: <Text id={`formattedTransferIraContributionTypes.${curr.type}`} /> }), {})}
              id="transferIraContributionType"
            />
          </FormGroup>

          {
            showApplyToPriorYearField &&
            <>
              <label htmlFor="applyToPriorYear">
                <Text id="fundYourAccountComponent.isThisContributionForThePriorYear" data={{ year: new Date().getFullYear() - 1 }} />
              </label>
              <FormGroup>
                <Field
                  component={StepperField}
                  id="applyToPriorYear"
                  options={['yes', 'no'].map((option) => ({ value: option === 'yes' ? true : false, label: <Text id={option} /> }))}
                />
              </FormGroup>
            </>
          }
        </>
      ))}

      {showErrorSubmittingFundingRequest && <p className="error"><Text id="fundYourAccountComponent.issueSubmittingFundingRequest" type="error" /></p>}

      <Button className="btn-primary" type="submit"><Text id="btns.submitFundingRequest" /></Button>
    </Form>
  );
};

export default FundingRequestStepForm;